/**
 * L'activation manuelle depuis le back-office (docs/10 §8).
 *
 * **La seule façon de vendre en v1** : `ManualProvider` ne facture rien, et
 * c'est un administrateur qui ouvre un plan. Ces hooks exigent
 * `billing.subscription.manage_any` — masquée côté écran par `RequirePermission`,
 * et **vérifiée côté serveur**, qui reste seul juge.
 */
import { useMutation, useQuery, useQueryClient, type UseQueryResult } from "@tanstack/react-query";

import type { Entitlements, Subscription } from "@/features/billing/api/useBilling";
import { api, unwrap } from "@/shared/api/client";
import type { ApiError } from "@/shared/api/errors";
import { billingKeys } from "@/shared/api/query-keys";

export function useUserEntitlements(userId: string): UseQueryResult<Entitlements, ApiError> {
  return useQuery({
    queryKey: billingKeys.userEntitlements(userId),
    // `retry: false` : un compte dont on n'a pas le droit de lire l'offre rend
    // 403, et le réessayer ne changera pas la réponse.
    retry: false,
    queryFn: () =>
      unwrap(
        api.GET("/api/v1/admin/billing/users/{user_id}/entitlements", {
          params: { path: { user_id: userId } },
        }),
      ),
  });
}

interface ActivateInput {
  userId: string;
  planCode: string;
}

/**
 * Ouvre un abonnement au nom d'un utilisateur.
 *
 * Invalide **la racine `billing` entière**, et pas seulement la fiche visée :
 * si l'administrateur active son propre plan, ses droits changent aussi, et
 * n'invalider que `userEntitlements(userId)` laisserait sa propre interface
 * verrouillée jusqu'au prochain rechargement.
 */
export function useActivateSubscription() {
  const queryClient = useQueryClient();
  return useMutation<Subscription, ApiError, ActivateInput>({
    mutationFn: ({ userId, planCode }) =>
      unwrap(
        api.POST("/api/v1/admin/billing/users/{user_id}/subscription", {
          params: { path: { user_id: userId } },
          body: { plan_code: planCode },
        }),
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: billingKeys.all }),
  });
}

export function useChangeUserPlan() {
  const queryClient = useQueryClient();
  return useMutation<Subscription, ApiError, ActivateInput>({
    mutationFn: ({ userId, planCode }) =>
      unwrap(
        api.POST("/api/v1/admin/billing/users/{user_id}/subscription/plan", {
          params: { path: { user_id: userId } },
          body: { plan_code: planCode },
        }),
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: billingKeys.all }),
  });
}

export function useCancelUserSubscription() {
  const queryClient = useQueryClient();
  return useMutation<Subscription, ApiError, { userId: string }>({
    mutationFn: ({ userId }) =>
      unwrap(
        api.POST("/api/v1/admin/billing/users/{user_id}/subscription/cancel", {
          params: { path: { user_id: userId } },
          body: { at_period_end: false },
        }),
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: billingKeys.all }),
  });
}
